export type DiffEntry =
  | { kind: "changed"; path: string; previous: unknown; current: unknown }
  | { kind: "added"; path: string; current: unknown }
  | { kind: "removed"; path: string; previous: unknown };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function arraysDeepEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!deepEqual(a[i], b[i])) return false;
  }
  return true;
}

function objectsDeepEqual(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }
  return true;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) return arraysDeepEqual(a, b);
  if (isPlainObject(a) && isPlainObject(b)) return objectsDeepEqual(a, b);
  return false;
}

function joinPath(prefix: string, segment: string): string {
  if (!prefix) return segment;
  if (segment.startsWith("[")) return `${prefix}${segment}`;
  return `${prefix}.${segment}`;
}

/**
 * Computes a structured diff between two JSON values.
 * Paths use dot notation for objects and [i] for arrays: "tables[0].name".
 * Arrays are diffed positionally (not by identity).
 * Returns an empty array when previous and current are deeply equal.
 */
export function diffJson(
  previous: unknown,
  current: unknown,
  pathPrefix: string = "",
): DiffEntry[] {
  if (deepEqual(previous, current)) return [];

  // Both objects — recurse on union of keys
  if (isPlainObject(previous) && isPlainObject(current)) {
    return diffObjects(previous, current, pathPrefix);
  }

  // Both arrays — positional diff
  if (Array.isArray(previous) && Array.isArray(current)) {
    return diffArrays(previous, current, pathPrefix);
  }

  // Mixed types or primitives — single changed entry at this path
  // (path may be empty for root-level primitive changes)
  return [{ kind: "changed", path: pathPrefix, previous, current }];
}

function diffObjects(
  previous: Record<string, unknown>,
  current: Record<string, unknown>,
  pathPrefix: string,
): DiffEntry[] {
  const out: DiffEntry[] = [];
  const keys = new Set([...Object.keys(previous), ...Object.keys(current)]);
  for (const key of keys) {
    const inPrev = Object.prototype.hasOwnProperty.call(previous, key);
    const inCurr = Object.prototype.hasOwnProperty.call(current, key);
    const nextPath = joinPath(pathPrefix, key);
    if (inPrev && !inCurr) {
      out.push({ kind: "removed", path: nextPath, previous: previous[key] });
    } else if (!inPrev && inCurr) {
      out.push({ kind: "added", path: nextPath, current: current[key] });
    } else if (!deepEqual(previous[key], current[key])) {
      out.push(...diffJson(previous[key], current[key], nextPath));
    }
  }
  return out;
}

function diffArrays(previous: unknown[], current: unknown[], pathPrefix: string): DiffEntry[] {
  const out: DiffEntry[] = [];
  const max = Math.max(previous.length, current.length);
  for (let i = 0; i < max; i++) {
    const indexPath = joinPath(pathPrefix, `[${i}]`);
    if (i >= current.length) {
      out.push({ kind: "removed", path: indexPath, previous: previous[i] });
    } else if (i >= previous.length) {
      out.push({ kind: "added", path: indexPath, current: current[i] });
    } else if (!deepEqual(previous[i], current[i])) {
      out.push(...diffJson(previous[i], current[i], indexPath));
    }
  }
  return out;
}
